import React, { useEffect, useState } from 'react';
import {
    Container,
    Grid,
    Typography,
    CircularProgress,
    Alert,
    Box,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel
} from '@mui/material';
import { fetchAuthSession } from 'aws-amplify/auth';
import TraderCard from '../components/TraderCard';
import { useInView } from 'react-intersection-observer';

function TradingLeaderboard() {
    const [leaders, setLeaders] = useState([]);
    const [filteredLeaders, setFilteredLeaders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('provenAlpha');
    const [showActiveOnly, setShowActiveOnly] = useState(true);
    const [visibleItems, setVisibleItems] = useState(10);
    const [trackedMembers, setTrackedMembers] = useState([]);
    const [isPremium, setIsPremium] = useState(false);

    const { ref, inView } = useInView({
        threshold: 0.3,
    });

    // Fetch tracked members
    const fetchTrackedMembers = async (idToken) => {
        try {
            const response = await fetch('https://api.capitolgains.io/user/track-member', {
                headers: { Authorization: `Bearer ${idToken}` },
            });
            if (!response.ok) throw new Error('Failed to fetch tracked members');
            const data = await response.json();
            setTrackedMembers(data.tracked_members || []);
        } catch (error) {
            console.error('Error fetching tracked members:', error);
            // Don't set error state here to avoid disrupting the main view
        }
    };

    // Update tracked members state when a member is tracked/untracked
    const handleTrackingUpdate = (memberId, isCurrentlyTracked) => {
        setTrackedMembers(prev => 
            isCurrentlyTracked 
                ? prev.filter(id => id !== memberId)
                : [...prev, memberId]
        );
    };

    // Fetch session data and initialize data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const session = await fetchAuthSession();
                if (session && session.tokens && session.tokens.idToken) {
                    const idToken = session.tokens.idToken.toString();

                    // Derive premium status from the already-fetched session token
                    const groups = session.tokens.idToken.payload?.['cognito:groups'] || [];
                    setIsPremium(groups.includes('Premium'));

                    // Fetch leaders and tracked members in parallel
                    const [leadersResponse] = await Promise.all([
                        fetch('https://api.capitolgains.io/user/congress', {
                            headers: { Authorization: `Bearer ${idToken}` },
                        }),
                        fetchTrackedMembers(idToken)
                    ]);

                    if (!leadersResponse.ok) throw new Error('Network response was not ok');
                    const data = await leadersResponse.json();
                    const enrichedLeaders = (data.trader_stats || []).map(member => {
                        const score = (member.avg_returns?.stocks?.purchase?.roi || 0) * 100 * (member.bullish_transactions?.accuracy || 0);
                        return { ...member, score };
                    });
                    setLeaders(enrichedLeaders);
                    setLoading(false);
                } else throw new Error('Session is invalid or missing ID token');
            } catch (error) {
                setError(error);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Apply sorting and filtering
    useEffect(() => {
        const getSortValue = (member) => {
            const avgReturns = member.avg_returns || {};
            const stocksReturns = avgReturns.stocks || {};
            const purchaseROI = stocksReturns.purchase ? stocksReturns.purchase.roi : 0;
            const purchaseCount = member.bullish_transactions?.count || 0;
            const accuracy = member.bullish_transactions?.accuracy || 0;
            const purchaseVolume = member.bullish_transactions?.volume || 0;
            const profitEstimated = member.bullish_transactions?.profit_estimated || 0;

            const alpha = member.bullish_transactions?.alpha || 0;

            // The 5% lower bound of the empirical-Bayes posterior, computed
            // backend-side -- a conservative floor, not the posterior mean
            // (that is member.alpha_adjusted, which reads higher). Ranking by
            // RAW alpha put members with a single lucky trade at the top --
            // the card even rendered "Insufficient Data" for someone ranked
            // 3rd. Members with no bound sort last rather than first.
            const provenAlpha = member.alpha_lower_bound;

            switch (sortBy) {
                case 'provenAlpha':
                    // Graceful degradation: alpha_lower_bound only exists in
                    // trader_stats.json after the Rankings step has run with
                    // the empirical-Bayes change. Until then fall back to the
                    // CG score, which is also sample-weighted. Returning
                    // -Infinity for every member would make the comparator
                    // evaluate (-Inf) - (-Inf) = NaN and leave the sort order
                    // undefined.
                    if (provenAlpha != null) return provenAlpha;
                    if (member.score != null) return member.score - 1000;
                    return -1e9;
                case 'alpha':
                    // Explicit raw-alpha sort still available, but members
                    // without enough data to be scored are pushed to the
                    // bottom instead of dominating the top.
                    return member.score === null || member.score === undefined
                        ? -Infinity
                        : alpha;
                case 'averageROI':
                    return purchaseROI;
                case 'hitRate':
                    return accuracy;
                case 'numberOfPurchases':
                    return purchaseCount;
                case 'averageStockPurchase':
                    return purchaseVolume;
                case 'estimatedProfit':
                    return profitEstimated;
                default:
                    return alpha;
            }
        };

        const filteredAndSortedLeaders = leaders
            .filter(member => showActiveOnly ? member.member_details?.current_member === true : true)
            .sort((a, b) => getSortValue(b) - getSortValue(a));

        setFilteredLeaders(filteredAndSortedLeaders);
    }, [leaders, sortBy, showActiveOnly]);

    // Lazy load additional items
    useEffect(() => {
        if (inView && visibleItems < filteredLeaders.length) {
            setVisibleItems(prevVisibleItems => prevVisibleItems + 10);
        }
    }, [inView, visibleItems, filteredLeaders.length]);

    if (loading) return (
        <Container sx={{ pt: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
        </Container>
    );
    if (error) return (
        <Container sx={{ pt: 6 }}>
            <Alert severity="error">Error: {error.message}</Alert>
        </Container>
    );

    return (
        <Container sx={{ pt: 6, pb: 8 }}>
            <Typography variant="h1" sx={{ color: 'text.primary', mb: 0.5 }}>
                Congressional Leaderboard
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Members of Congress ranked by their disclosed stock trading performance.
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1.5,
                    mb: 3,
                }}
            >
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
                    <InputLabel id="sort-by-label">Sort by</InputLabel>
                    <Select
                        labelId="sort-by-label"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        label="Sort by"
                    >
                        <MenuItem value="provenAlpha">Proven Alpha (recommended)</MenuItem>
                        <MenuItem value="alpha">Alpha vs S&P 500 (raw)</MenuItem>
                        <MenuItem value="averageROI">Annual Return</MenuItem>
                        <MenuItem value="hitRate">Hit Rate</MenuItem>
                        <MenuItem value="numberOfPurchases">Number of Purchases</MenuItem>
                        <MenuItem value="averageStockPurchase">Average Position Size</MenuItem>
                        <MenuItem value="estimatedProfit">Estimated Profit</MenuItem>
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={showActiveOnly}
                            onChange={(e) => setShowActiveOnly(e.target.checked)}
                        />
                    }
                    label="Active members only"
                    sx={{
                        color: 'text.secondary',
                        ml: 0,
                        '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                    }}
                />
            </Box>

            <Grid container spacing={2}>
                {filteredLeaders.length === 0 ? (
                    <Grid item xs={12}>
                        <Alert severity="info">No data available.</Alert>
                    </Grid>
                ) : (
                    filteredLeaders.slice(0, visibleItems).map((member, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <TraderCard
                                member={member}
                                index={index}
                                isTracked={trackedMembers.includes(member.member_details?.bioguideId)}
                                onTrackingUpdate={handleTrackingUpdate}
                                isPremium={isPremium}
                            />
                        </Grid>
                    ))
                )}
            </Grid>
            {visibleItems < filteredLeaders.length && (
                <Box ref={ref} sx={{ display: 'flex', justifyContent: 'center', mt: 2, minHeight: 40 }} />
            )}
        </Container>
    );
}

export default TradingLeaderboard;